import { useEffect, useRef, useState } from "react";

import CircleButton from "../CircleButton";
import DisplayError from "../DisplayError";
import { FACING_MODES, IMAGE_TYPES } from "../lib";
import WhiteFlash from "../WhiteFlash";
import { getShowHideStyle, getVideoStyles, playClickAudio, printCameraInfo } from "./helpers";
import { useLibCameraPhoto } from "./hooks/useLibCameraPhoto";
import "./styles/camera.css";

export type CameraFacingMode = (typeof FACING_MODES)[keyof typeof FACING_MODES];
export type CameraImageType = (typeof IMAGE_TYPES)[keyof typeof IMAGE_TYPES];

export interface CameraProps {
	onTakePhoto?: (dataUri: string) => void;
	onTakePhotoAnimationDone?: (dataUri: string) => void;
	onCameraError?: (error: Error) => void;
	onCameraStart?: (mediaStream: MediaStream) => void;
	onCameraStop?: () => void;
	idealFacingMode?: CameraFacingMode;
	idealResolution?: { width: number; height: number };
	imageType?: CameraImageType;
	isImageMirror?: boolean;
	isSilentMode?: boolean;
	isDisplayStartCameraError?: boolean;
	imageCompression?: number;
	isMaxResolution?: boolean;
	isFullscreen?: boolean;
	sizeFactor?: number;
}

const SHOW_VIDEO_DELAY = 900;

export function Camera({
	onTakePhoto,
	onTakePhotoAnimationDone,
	onCameraError,
	onCameraStart,
	onCameraStop,
	idealFacingMode,
	idealResolution,
	imageType,
	isImageMirror,
	isSilentMode = false,
	isDisplayStartCameraError = true,
	imageCompression,
	isMaxResolution = false,
	isFullscreen = false,
	sizeFactor,
}: CameraProps) {
	const [dataUri, setDataUri] = useState("");
	const [isShowVideo, setIsShowVideo] = useState(true);
	const [cameraStartDisplayError, setCameraStartDisplayError] = useState("");
	const showVideoTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
	const videoRef = useRef<HTMLVideoElement>(null);
	const [mediaStream, cameraStartError, cameraStopError, getDataUri] = useLibCameraPhoto(
		videoRef,
		idealFacingMode,
		idealResolution,
		isMaxResolution,
	);

	useEffect(() => {
		if (mediaStream) onCameraStart?.(mediaStream);
		else onCameraStop?.();
	}, [mediaStream, onCameraStart, onCameraStop]);

	useEffect(() => {
		if (!cameraStartError) return;
		setCameraStartDisplayError(`${cameraStartError.name} ${cameraStartError.message}`);
		onCameraError?.(cameraStartError);
	}, [cameraStartError, onCameraError]);

	useEffect(() => {
		if (cameraStopError) printCameraInfo(cameraStopError.message);
	}, [cameraStopError]);

	useEffect(
		() => () => {
			if (showVideoTimeoutRef.current) clearTimeout(showVideoTimeoutRef.current);
		},
		[],
	);

	const isImageMirrored = isImageMirror ?? idealFacingMode === FACING_MODES.USER;

	function handleTakePhoto() {
		const photo = getDataUri({ sizeFactor, imageType, imageCompression, isImageMirror: isImageMirrored });

		if (!isSilentMode) playClickAudio();
		onTakePhoto?.(photo);
		setDataUri(photo);
		setIsShowVideo(false);

		if (showVideoTimeoutRef.current) clearTimeout(showVideoTimeoutRef.current);
		showVideoTimeoutRef.current = setTimeout(() => {
			setIsShowVideo(true);
			onTakePhotoAnimationDone?.(photo);
		}, SHOW_VIDEO_DELAY);
	}

	const className = `react-html5-camera-photo${isFullscreen ? " react-html5-camera-photo-fullscreen" : ""}`;

	return (
		<div className={className}>
			<DisplayError
				cssClass="display-error"
				isDisplayError={isDisplayStartCameraError}
				errorMsg={cameraStartDisplayError}
			/>
			<WhiteFlash isShowWhiteFlash={!isShowVideo} />
			<img style={getShowHideStyle(!isShowVideo)} alt="camera" src={dataUri} />
			<video style={getVideoStyles(isShowVideo, isImageMirrored)} ref={videoRef} autoPlay muted playsInline />
			<CircleButton isClicked={!isShowVideo} onClick={handleTakePhoto} />
		</div>
	);
}

export { FACING_MODES, IMAGE_TYPES };

export default Camera;
